import { useCallback, useMemo, useState } from 'react'
import { PositionDrawingFilterState } from '../screens/admin/position-drawing/components/positionDrawingFilters'
import { PositionDrawingTableFilters } from '../screens/admin/position-drawing/positionDrawingTable'
import {
  defaultPositionDrawingFilterState,
  getPositionDrawingQueryCategoryIds,
  toPositionDrawingTableFilters,
} from './position-drawing-filter.action'
import { useDebouncedSearchInput } from './useDebouncedSearchInput'

export interface UsePositionDrawingFiltersResult {
  filters: PositionDrawingFilterState
  debouncedSearch: string
  queryCategoryIds: EntityId[] | undefined
  tableFilters: PositionDrawingTableFilters
  onSearchChange: (value: string) => void
  onProductCategoriesChange: (productCategoryIds: EntityId[]) => void
  setFilters: React.Dispatch<React.SetStateAction<PositionDrawingFilterState>>
}

export const usePositionDrawingFilters = (
  initialState: PositionDrawingFilterState = defaultPositionDrawingFilterState,
): UsePositionDrawingFiltersResult => {
  const [filters, setFilters] = useState<PositionDrawingFilterState>(initialState)
  const debouncedSearch = useDebouncedSearchInput(filters.searchInput)

  const queryCategoryIds = useMemo(
    () => getPositionDrawingQueryCategoryIds(filters.productCategoryIds),
    [filters.productCategoryIds],
  )

  const tableFilters = useMemo(
    () => toPositionDrawingTableFilters(debouncedSearch, filters.productCategoryIds),
    [debouncedSearch, filters.productCategoryIds],
  )

  const onSearchChange = useCallback((value: string) => {
    setFilters(previous => ({ ...previous, searchInput: value }))
  }, [])

  const onProductCategoriesChange = useCallback((productCategoryIds: EntityId[]) => {
    setFilters(previous => ({ ...previous, productCategoryIds }))
  }, [])

  return {
    filters,
    debouncedSearch,
    queryCategoryIds,
    tableFilters,
    onSearchChange,
    onProductCategoriesChange,
    setFilters,
  }
}
